
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { NotificationSetting, asNotificationSettings } from "@/types/settings";

export const useNotificationSettings = () => {
  // Default settings to use when none exist in the database
  const defaultSettings: NotificationSetting[] = [
    {
      key: 'document_verified',
      label: 'Document Verification',
      description: 'Send notifications when a user\'s document is approved or rejected',
      email: true,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'new_poll_published',
      label: 'New Poll Published',
      description: 'Notify verified pollsters when a new poll is published',
      email: false,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'pollster_status_updated',
      label: 'Pollster Status Updated',
      description: 'Notify users when they are added as a pollster or their status changes',
      email: true,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'poll_closed',
      label: 'Poll Closed',
      description: 'Notify participants when a poll they voted in is closed and analyzed',
      email: false,
      inApp: true,
      frequency: 'immediate',
    },
  ];
  
  const [settings, setSettings] = useState<NotificationSetting[]>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Use maybeSingle() instead of single() to avoid 406 errors when no record exists
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('key', 'notification_settings')
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching notification settings:', error);
        // Use default settings on error
        setSettings(defaultSettings);
        return;
      }
      
      // If we have data, use it, otherwise use defaults
      if (data?.value) {
        setSettings(asNotificationSettings(data.value));
      } else {
        console.log('No notification settings found, using defaults');
        setSettings(defaultSettings);
      }
      
    } catch (error) {
      console.error('Error in fetchSettings:', error);
      // Use default settings on error
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmail = (index: number) => {
    const newSettings = [...settings];
    newSettings[index].email = !newSettings[index].email;
    setSettings(newSettings);
  };

  const handleToggleInApp = (index: number) => {
    const newSettings = [...settings];
    newSettings[index].inApp = !newSettings[index].inApp;
    setSettings(newSettings);
  };

  const handleFrequencyChange = (index: number, value: "immediate" | "daily" | "never") => {
    const newSettings = [...settings];
    newSettings[index].frequency = value;
    setSettings(newSettings);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Convert the settings array to a plain object that can be stored as JSON
      const settingsValue = settings.map(setting => ({
        key: setting.key,
        label: setting.label,
        description: setting.description,
        email: setting.email,
        inApp: setting.inApp,
        frequency: setting.frequency
      }));
      
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: 'notification_settings',
            value: settingsValue
          },
          {
            onConflict: 'key'
          }
        );
        
      if (error) throw error;
      
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    loading,
    saving,
    handleToggleEmail,
    handleToggleInApp,
    handleFrequencyChange,
    saveSettings,
  };
};
